import type { ScriptStep } from '../types/steps';
import type {
  FunctionSpec,
  RangeSpec,
  TestPoint
} from '../data/defaultCatalog';

type SelectedTestPoint = {
  func: FunctionSpec;
  range: RangeSpec;
  testPoint: TestPoint;
};

const SETTLING_MS = 1500;

export const generateStepsFromTestPoints = (
  selected: SelectedTestPoint[]
): ScriptStep[] => {
  const steps: ScriptStep[] = [];

  // Group by function then range to minimize mode switches
  const byFunction = new Map<string, SelectedTestPoint[]>();
  for (const tp of selected) {
    const key = tp.func.id;
    if (!byFunction.has(key)) byFunction.set(key, []);
    byFunction.get(key)!.push(tp);
  }

  for (const [, points] of byFunction) {
    // Sort by range full scale, then by nominal value
    points.sort((a, b) => {
      if (a.range.fullScale !== b.range.fullScale)
        return a.range.fullScale - b.range.fullScale;
      return a.testPoint.nominal - b.testPoint.nominal;
    });

    for (const { func, testPoint } of points) {
      // 1. Set robot output
      steps.push({
        action: 'set-output',
        timeoutMs: 10000,
        maxRetries: 3
      });

      // 2. Wait for settling
      steps.push({
        durationMs: SETTLING_MS,
        label: `Settling — ${testPoint.label} (${func.name})`
      });

      // 3. Record measurement
      steps.push({
        type: 'measure',
        action: 'read',
        resultMapping: {
          targetFunctionId: func.id,
          targetTestPointId: testPoint.id,
          targetField: 'measuredAsFound',
          sourcePayloadPath: 'reading.value'
        },
        timeoutMs: 15000,
        maxRetries: 3,
        onFailure: 'pause'
      });

      // 4. Standby between points
      steps.push({
        action: 'standby',
        timeoutMs: 10000,
        onFailure: 'continue'
      });
    }
  }

  return steps;
};
